import { useCallback, useEffect, useRef, useState } from "react";
import { FEED_SHEET_QUERY, feedHandleLabel, feedStorageKey, parseFeedCollapsed, serializeFeedCollapsed, sheetGesture } from "../lib/feedDrawer";

function readSheet(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(FEED_SHEET_QUERY).matches : false;
}

function readCollapsed(sheet: boolean): boolean {
  try {
    return parseFeedCollapsed(localStorage.getItem(feedStorageKey(sheet)), sheet);
  } catch {
    return sheet; // blocked storage: the default for this layout
  }
}

/**
 * Whether the live packet feed is folded. A drawer on the map's right edge on a wide screen, a bottom sheet on a
 * phone; each remembers its own choice per viewer (localStorage, never required). Picking something on the map
 * (`selectionActive`) opens it, because the detail panel lives in the same column; that opening is not remembered.
 */
export function useFeedDrawer(selectionActive: boolean) {
  const [sheet, setSheet] = useState(readSheet);
  const [collapsed, setCollapsedState] = useState(() => readCollapsed(readSheet()));

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(FEED_SHEET_QUERY);
    const on = (e: MediaQueryListEvent) => {
      setSheet(e.matches);
      setCollapsedState(readCollapsed(e.matches));
    };
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);

  const setCollapsed = useCallback(
    (next: boolean) => {
      setCollapsedState(next);
      try {
        localStorage.setItem(feedStorageKey(sheet), serializeFeedCollapsed(next));
      } catch {
        /* private mode or blocked storage: it still folds, it just is not remembered */
      }
    },
    [sheet],
  );

  useEffect(() => {
    if (selectionActive) setCollapsedState(false);
  }, [selectionActive]);

  return { collapsed, setCollapsed, toggle: () => setCollapsed(!collapsed) };
}

/** The folded drawer on a wide screen: a narrow tab down the map's right edge. */
export function FeedTab({ count, onOpen }: { count: number | null; onOpen: () => void }) {
  return (
    <button type="button" className="feed-tab" onClick={onOpen} aria-label={`Open ${feedHandleLabel(count)}`} aria-expanded={false}>
      <span className="feed-tab-chevron" aria-hidden="true">
        ‹
      </span>
      <span className="feed-tab-label">{feedHandleLabel(count)}</span>
    </button>
  );
}

/** The bar of the phone sheet: always visible; tap, or drag up or down, to open or fold. */
export function FeedBar({ count, collapsed, onSet }: { count: number | null; collapsed: boolean; onSet: (collapsed: boolean) => void }) {
  const startY = useRef<number | null>(null);
  function finish(clientY: number) {
    if (startY.current === null) return;
    const g = sheetGesture(clientY - startY.current, collapsed);
    startY.current = null;
    if (g === "toggle") onSet(!collapsed);
    else if (g === "expand") onSet(false);
    else if (g === "collapse") onSet(true);
  }
  return (
    <button
      type="button"
      className="feed-bar"
      aria-expanded={!collapsed}
      aria-label={collapsed ? `Open ${feedHandleLabel(count)}` : `Fold ${feedHandleLabel(count)}`}
      onPointerDown={(e) => {
        startY.current = e.clientY;
      }}
      onPointerUp={(e) => finish(e.clientY)}
      onPointerCancel={() => {
        startY.current = null;
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSet(!collapsed);
        }
      }}
      onClick={(e) => {
        // A keyboard or assistive-tech activation arrives as a click with no pointer sequence behind it.
        if (e.detail === 0) onSet(!collapsed);
      }}
    >
      <span className="feed-bar-grip" aria-hidden="true" />
      <span className="feed-bar-label">{feedHandleLabel(count)}</span>
    </button>
  );
}
